import { v1 as uuid } from 'uuid';
import { Box, FormHelperText, FormControl, makeStyles } from '@material-ui/core';
import { forEach, isArray, isObject, orderBy } from 'lodash';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import jsonpath from 'jsonpath';
import { getRequiredScopes, useAuthenticationContext } from '../../../../authentication';
import { siteActions } from '../../../../store/actions';
import { DropDownField } from '../../../../store/modules/application/types';
import { Api } from '../../../../store/services/services';
import { composeParamsForSearch } from '../../../../utils/query';
import AutoCompleteSelect from '../../../AutoCompleteSelect';
import { FormFieldProps } from '../../../FormDisplay/FormFieldDisplay/FormFieldDisplay';

const MultiSelectFilterField: FunctionComponent<FormFieldProps> = (props) => {
    const { formField, value, handleInputChange, formData, error } = props;

    const classes = makeStyles(() => ({
        formControl: {
            marginLeft: 15,
            minWidth: 160,
            maxWidth: 160,
        },
        autoCompleteInput: {
            width: '130px !important',
        },
        autoCompleteInputRoot: {
            width: '165px',
            maxHeight: '55px',
            overflow: 'hidden',
            display: 'inline-block',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        autoCompleteEndAdornment: {
            top: 'calc(50% - 14px)',
            right: 2,
            position: 'absolute',
        },
        autoCompletePaper: {
            width: '250px',
        },
        autoCompleteRoot: {
            marginTop: '3px !important',
        },
    }))();

    const [choices, setChoices] = useState<DropDownField[]>([]);
    const authContext = useAuthenticationContext();
    const { scopes, getAccessToken } = authContext;
    const dispatch = useDispatch();

    const getFieldChoices = useCallback(async () => {
        let choices: DropDownField[] = [];

        if (formField.api) {
            let url: string = formField.api.url;
            if (formField.api.queryParams) {
                const queryParams = composeParamsForSearch(formField.api.queryParams);
                url = `${url}?${queryParams}`;

                if (url && queryParams.includes('_fact') && formField.typeId === 'choices') {
                    const dependantFieldName = jsonpath.value(formField?.api?.queryParams, '$..fact');
                    const dependantFieldValue = jsonpath.value(formData, dependantFieldName);
                    const payload = JSON.stringify({ inq: dependantFieldValue });
                    // Removing un-necessary characters from the url
                    url = url
                        .replace(dependantFieldName, payload)
                        .replace(/(\?")/g, '?')
                        .replace(/("=)/g, '=')
                        .replace(/_fact/, '')
                        .replace(/(:"{)/g, ':{')
                        .replace(/(}")/g, '}');
                }
            }
            const scope = getRequiredScopes(url);
            if (!!scope) {
                try {
                    const token = await getAccessToken({ scopes: [scopes[scope]] }).then(
                        (response) => response.accessToken,
                    );

                    let data = await Api.getChoices(url, token);

                    if (isArray(data)) {
                        if (formField.api.resultMapping) {
                            choices = data.map((choice) => {
                                let choiceMap: any = {};
                                forEach(formField?.api?.resultMapping!, (mapValue, mapKey) => {
                                    choiceMap[mapKey] = choice[mapValue];
                                });
                                return choiceMap;
                            });
                        } else if (data.some((choice) => !isObject(choice))) {
                            choices = data.map((choice) => ({
                                label: choice,
                                value: choice,
                            }));
                        } else choices = data;
                    }
                } catch (error) {
                    dispatch(siteActions.setError(error));
                }
                setChoices(orderBy(choices, 'label', 'asc'));
            }
        } else setChoices(orderBy(formField.choices, 'label', 'asc'));
    }, [formField, formData, scopes, getAccessToken, dispatch]);

    useEffect(() => {
        getFieldChoices();
    }, [getFieldChoices]);

    const selectedValues =
        value && choices?.filter((choice: DropDownField) => value.some((val: unknown) => val === choice.value));
    return (
        <Box>
            <FormControl className={classes.formControl}>
                {
                    <>
                        <AutoCompleteSelect
                            size="small"
                            inputSize={'small'}
                            variant={'standard'}
                            isMultipleValue={true}
                            label={formField.label}
                            disableCloseOnSelect={true}
                            disableClearable={true}
                            data={choices}
                            classes={{
                                root: classes.autoCompleteRoot,
                                input: classes.autoCompleteInput,
                                inputRoot: classes.autoCompleteInputRoot,
                                endAdornment: classes.autoCompleteEndAdornment,
                                paper: classes.autoCompletePaper,
                            }}
                            onChange={(data: DropDownField[]) =>
                                handleInputChange(
                                    formField.field,
                                    data.map((obj) => obj.value),
                                )
                            }
                            value={selectedValues || []}
                        />
                        {error?.messages?.map((msg) => (
                            <FormHelperText key={uuid()} error={true}>
                                {msg}
                            </FormHelperText>
                        ))}
                    </>
                }
            </FormControl>
        </Box>
    );
};

export default MultiSelectFilterField;
